import { useState } from "react";
import { Alert, Box, Button, Card, Link, TextField, Typography } from "@mui/material";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import { useSession } from "../context/SessionContext";
import { confirmSignUp, resendCode, signUp } from "../services/cognitoAuthService";

/**
 * Sign in, create an account (with the emailed confirmation code), and the "choose a new password"
 * step for users an admin created directly. A new account has no role until an admin approves it.
 */
export default function LoginPage() {
  const { login, setNewPassword } = useSession();
  const [mode, setMode] = useState("signin"); // signin | signup | confirm
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPasswordValue] = useState("");
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  function go(next) {
    setMode(next);
    setError("");
    setInfo("");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setInfo("");
    setBusy(true);
    try {
      if (needsNewPassword) {
        await setNewPassword(newPassword);
      } else if (mode === "signup") {
        await signUp(name.trim(), email.trim(), password);
        setMode("confirm");
        setInfo(`We emailed a confirmation code to ${email.trim()}.`);
      } else if (mode === "confirm") {
        await confirmSignUp(email.trim(), code);
        setMode("signin");
        setCode("");
        setInfo("Email confirmed. Sign in below. An administrator must approve your account before you can see anything.");
      } else {
        const result = await login(email.trim(), password);
        if (result.newPasswordRequired) setNeedsNewPassword(true);
      }
    } catch (e) {
      if (e?.code === "UserNotConfirmedException") {
        setMode("confirm");
        setInfo("Your email is not confirmed yet. Enter the code we emailed you, or request a new one.");
      } else {
        setError(e?.message || "That did not work. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function sendAgain() {
    setError("");
    try {
      await resendCode(email.trim());
      setInfo("A new code is on its way.");
    } catch (e) {
      setError(e?.message || "Could not send a new code.");
    }
  }

  const heading = needsNewPassword ? "Choose a new password"
    : mode === "signup" ? "Create your account"
    : mode === "confirm" ? "Confirm your email" : "Sign in";
  const lead = needsNewPassword ? "This is your first sign-in. Set a password to continue."
    : mode === "signup" ? "An administrator reviews every new account before it can be used."
    : mode === "confirm" ? "Enter the 6-digit code from the email we sent you."
    : "Use the email you signed up with.";
  const submitLabel = needsNewPassword ? "Save password and continue"
    : mode === "signup" ? "Create account" : mode === "confirm" ? "Confirm email" : "Sign in";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: 2,
        background:
          "radial-gradient(900px 500px at 15% 10%, rgba(99,102,241,0.55), transparent 60%)," +
          "radial-gradient(800px 500px at 90% 90%, rgba(14,165,233,0.45), transparent 60%)," +
          "linear-gradient(180deg, #0B1020, #141233)",
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 420, p: 4, borderRadius: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              color: "#fff",
              background: "linear-gradient(135deg, #6366F1, #A855F7 55%, #0EA5E9)",
            }}
          >
            <ShieldRoundedIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.1 }}>
              VeraInsight GRC
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Governance, risk and compliance
            </Typography>
          </Box>
        </Box>

        <Typography variant="h5" sx={{ mb: 0.5 }}>
          {heading}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {lead}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
          {!needsNewPassword && mode === "signup" && (
            <TextField label="Full name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          )}
          {!needsNewPassword && (
            <TextField
              label="Email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={mode === "confirm"}
              autoFocus={mode === "signin"}
            />
          )}
          {!needsNewPassword && (mode === "signin" || mode === "signup") && (
            <TextField
              label="Password"
              type="password"
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              helperText={mode === "signup" ? "At least 12 characters with upper and lower case, a number and a symbol." : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
          {!needsNewPassword && mode === "confirm" && (
            <TextField label="Confirmation code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
          )}
          {needsNewPassword && (
            <TextField
              label="New password"
              type="password"
              autoComplete="new-password"
              helperText="At least 12 characters with upper and lower case, a number and a symbol."
              value={newPassword}
              onChange={(e) => setNewPasswordValue(e.target.value)}
              required
              autoFocus
            />
          )}
          {info && <Alert severity="info">{info}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" variant="contained" size="large" disabled={busy}>
            {busy ? "Please wait…" : submitLabel}
          </Button>
        </Box>

        {!needsNewPassword && (
          <Box sx={{ mt: 2.5, textAlign: "center", display: "grid", gap: 0.5 }}>
            {mode === "confirm" && (
              <Link component="button" type="button" onClick={sendAgain}>Send a new code</Link>
            )}
            {mode === "signin" ? (
              <Typography variant="body2" color="text.secondary">
                New here? <Link component="button" type="button" onClick={() => go("signup")}>Create an account</Link>
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                <Link component="button" type="button" onClick={() => go("signin")}>Back to sign in</Link>
              </Typography>
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
}
